import { useEffect, useRef, useState } from 'react'
import { cn } from '../utils/cn'
import ColoredBar from './coloredBar'

export default function Banner({
  images = [''],
  bannerMobile = '',
  title = '',
  subtitle = '',
  marca = null,
  hoveredBrand = null,
  brands = [],
}) {
  const [currentBanner, setCurrentBanner] = useState(0)
  const hasMultipleImages = images.length > 1

  // Refs para controlar o arrastar
  const bannerRef = useRef(null)
  const dragStartX = useRef(0)
  const isDragging = useRef(false)

  useEffect(() => {
    // Só ativa o carrossel se houver mais de uma imagem
    if (!hasMultipleImages) return

    const timer = setInterval(() => {
      setCurrentBanner(prev => (prev === images.length - 1 ? 0 : prev + 1))
    }, 8000)

    return () => clearInterval(timer)
  }, [hasMultipleImages, images.length])

  // Funções para controlar o arrastar do mouse/touch
  const handleDragStart = e => {
    if (!hasMultipleImages) return

    // Captura a posição inicial (seja mouse ou touch)
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX
    dragStartX.current = clientX
    isDragging.current = true
  }

  const handleDragMove = e => {
    if (!isDragging.current) return

    // Previne o comportamento padrão para evitar problemas
    e.preventDefault()
  }

  const handleDragEnd = e => {
    if (!isDragging.current || !hasMultipleImages) return

    // Captura a posição final (seja mouse ou touch)
    const clientX = e.type.includes('mouse')
      ? e.clientX
      : e.changedTouches && e.changedTouches[0]
        ? e.changedTouches[0].clientX
        : dragStartX.current

    // Calcula a distância arrastada
    const dragDistance = clientX - dragStartX.current
    const minDragDistance = 50 // Distância mínima para considerar um arrasto válido

    if (Math.abs(dragDistance) > minDragDistance) {
      if (dragDistance > 0) {
        // Arrasto para a direita - banner anterior
        setCurrentBanner(prev => (prev === 0 ? images.length - 1 : prev - 1))
      } else {
        // Arrasto para a esquerda - próximo banner
        setCurrentBanner(prev => (prev === images.length - 1 ? 0 : prev + 1))
      }
    }

    isDragging.current = false
  }

  // Função para cancelar o arrasto se o mouse sair da área
  const handleDragCancel = () => {
    isDragging.current = false
  }

  const isMobile = window.innerWidth < 768

  return (
    <div className="flex flex-col w-full">
      <div
        ref={bannerRef}
        className={cn(
          'bg-no-repeat bg-cover flex flex-col items-center justify-center p-4 relative md:static transition-all duration-500',
          hasMultipleImages ? 'cursor-grab' : '',
          isMobile
            ? `bg-[url('../assets/${bannerMobile}')] aspect-square`
            : `bg-[url('../assets/${images[currentBanner]}')] bg-bottom h-screen`,
        )}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragCancel}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onTouchCancel={handleDragCancel}
      >
        {/* Texto alinhado à esquerda */}
        {(title || subtitle) && (
          <div className="text-left text-white z-10 w-full max-w-[1400px] px-[10%]">
            {title && (
              <h1 className="text-[86px] font-titillium-web-bold mb-2 tracking-wider">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-2xl font-titillium-web tracking-wider">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {hasMultipleImages && !isMobile && (
          <div className="absolute bottom-8 flex gap-3 items-center">
            {images.map((_, index) => (
              <button
                type="button"
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`${
                  currentBanner === index ? 'w-[40px]' : 'w-[20px]'
                } h-[8px] rounded-full cursor-pointer transition-all duration-300 ${
                  currentBanner === index
                    ? 'bg-transparent border border-white'
                    : 'bg-white'
                }`}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Colored bar at the bottom of the banner */}
      <ColoredBar
        className="mt-0"
        hoveredBrand={hoveredBrand}
        brands={brands}
      />
    </div>
  )
}
